/**
 * Request context — module-level singleton.
 *
 * This module is the single source of truth for which tenant / agent is
 * active when an Axios request is dispatched.  It lives OUTSIDE React so
 * the Axios interceptor (which is also outside React) can read it without
 * importing the Zustand store (which would create a circular dependency).
 *
 * Write path (called by ContextStore actions):
 *   ContextStore.setActiveTenant() → requestContext.setTenantId()
 *   ContextStore.setActiveAgent()  → requestContext.setAgentId()
 *
 * Read path (called by the Axios request interceptor):
 *   requestContext.getTenantId()  → x-tenant-id header
 *   requestContext.getAgentId()   → x-agent-id header
 *
 * NEVER import the Zustand store from this file (circular dep).
 */

let _tenantId: string | null = null;
let _agentId:  string | null = null;

export const requestContext = {
  // ── Reads (called by Axios interceptor) ─────────────────────────────────
  getTenantId: (): string | null => _tenantId,
  getAgentId:  (): string | null => _agentId,

  // ── Writes (called by ContextStore & auth store) ─────────────────────────
  setTenantId: (id: string | null): void => { _tenantId = id; },
  setAgentId:  (id: string | null): void => { _agentId  = id; },

  /**
   * Atomically switch tenant context.
   * Clears agent whenever tenant changes to prevent cross-tenant leakage.
   */
  switchTenant: (tenantId: string): void => {
    _tenantId = tenantId;
    _agentId  = null;        // agent belongs to a tenant → must be cleared
  },

  /** Full reset — called on logout */
  clear: (): void => {
    _tenantId = null;
    _agentId  = null;
  },

  /** Debug snapshot — useful in dev tools / tests */
  snapshot: () => ({ tenantId: _tenantId, agentId: _agentId }),
} as const;
